import React, { useCallback, useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import {
  FiAlertTriangle, FiDatabase, FiFolder, FiGrid, FiRefreshCw, FiRepeat, FiSliders, FiTag, FiTrendingUp, FiX,
} from "react-icons/fi";
import { HiOutlineCalculator } from "react-icons/hi";
import {
  Empty, Group, LiveDot, Pill, Section,
} from "../activity-logs/styles";
import { SegmentedControl } from "../activity-logs/components/primitives";
import { IOS } from "../activity-logs/labels";
import { ActionButton, CreditsPage, Tag } from "../creditos/styles";
import {
  PeriodPicker, RoundButton, Toolbar, ToolbarDivider,
} from "../creditos/components/ControlBar";
import { ScrolledTopBar, UpdatedAgo } from "../creditos/components/parts";
import { formatMonth } from "../creditos/dashboardData";
import EmptyState from "../../components/EmptyState";
import EmpresasFilter from "./components/EmpresasFilter";
import ReformaDashboard, { ReformaDashboardSkeleton } from "./dashboard/ReformaDashboard";
import SimulationDashboard from "./simulacao/SimulationDashboard";
import TransitionDashboard from "./transicao/TransitionDashboard";
import CashDashboard from "./caixa/CashDashboard";
import PriceDashboard from "./precos/PriceDashboard";
import CadastroPanel from "./precos/CadastroPanel";
import MonthlyDashboard from "./dados/MonthlyDashboard";
import CreditsDashboard from "./dados/CreditsDashboard";
import ParametrosDashboard from "./parametros/ParametrosDashboard";
import { MotionScope } from "./components/Motion";
import useReformaFiscalData from "./hooks/useReformaFiscalData";
import useFormacaoPreco from "./hooks/useFormacaoPreco";
import useEmpresasIndustriais from "./hooks/useEmpresasIndustriais";
import { ReformaParamsProvider, useReformaParams } from "./hooks/useReformaParams";

/**
 * Estrutura da tela: cabeçalho, abas e filtros seguem o padrão de Créditos.
 *
 * As 9 telas viram 7 abas, na ordem das perguntas: Preços e Dados agrupam duas telas
 * cada, com um seletor menor dentro da aba.
 *
 * `real`: precisa dos SPEDs processados. `filtered`: a tela usa período e empresa.
 */
const VIEWS = {
  dashboard: { filtered: true },
  simulacao: { real: true, filtered: true },
  "transicao-anual": { real: true, filtered: true },
  "impacto-caixa": { real: true, filtered: true },
  "formacao-preco": { real: true },
  "cadastro-preco": {},
  "dados-mensais": { real: true, filtered: true },
  "creditos-despesas": { real: true, filtered: true },
  parametros: { real: true },
};

const TABS = [
  { key: "dashboard", label: "Dashboard", icon: <FiGrid />, views: ["dashboard"] },
  { key: "simulacao", label: "Simulação", icon: <HiOutlineCalculator />, views: ["simulacao"] },
  { key: "transicao", label: "Transição", icon: <FiTrendingUp />, views: ["transicao-anual"] },
  { key: "caixa", label: "Caixa", icon: <FiRepeat />, views: ["impacto-caixa"] },
  {
    key: "precos",
    label: "Preços",
    icon: <FiTag />,
    views: ["formacao-preco", "cadastro-preco"],
    sub: [{ key: "formacao-preco", label: "Formação de preço" }, { key: "cadastro-preco", label: "Cadastro" }],
  },
  {
    key: "dados",
    label: "Dados",
    icon: <FiDatabase />,
    views: ["dados-mensais", "creditos-despesas"],
    sub: [{ key: "dados-mensais", label: "Dados mensais" }, { key: "creditos-despesas", label: "Créditos e despesas" }],
  },
  { key: "parametros", label: "Parâmetros", icon: <FiSliders />, views: ["parametros"] },
];

const NO_DATA_HINT = "Disponível após o processamento dos SPEDs deste projeto";
const currentProject = () => localStorage.getItem("vidal_selected_project");

const monthEnd = (key) => {
  const [y, m] = key.split("-").map(Number);
  return `${key}-${String(new Date(y, m, 0).getDate()).padStart(2, "0")}`;
};

// Com muitas abas, a linha rola em tela estreita em vez de estourar a página
const TabsScroll = styled.div`
  max-width: 100%;
  overflow-x: auto;
  scrollbar-width: none;
  &::-webkit-scrollbar { display: none; }
`;

function StateMessage({ icon, title, sub, action }) {
  return (
    <Section $first>
      <Group>
        <Empty>
          <div className="e-icon">{icon}</div>
          <div className="e-title">{title}</div>
          <div className="e-sub" style={action ? { marginBottom: 14 } : undefined}>{sub}</div>
          {action}
        </Empty>
      </Group>
    </Section>
  );
}

function ReformaTributariaContent({ project }) {
  const [view, setView] = useState("dashboard");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [selectedCompanies, setSelectedCompanies] = useState([]);
  const [allMonths, setAllMonths] = useState([]);

  const filtros = useMemo(() => ({
    dataInicio: from ? `${from}-01` : null,
    dataFim: to ? monthEnd(to) : null,
    empresas: selectedCompanies,
  }), [from, to, selectedCompanies]);

  const fiscalData = useReformaFiscalData(filtros);
  const { params: rawParams } = useReformaParams();
  // Uma só instância: marcar uma empresa em Parâmetros precisa valer para as outras abas
  const {
    empresas, loading: loadingEmpresas, error: empresasError, toggleIndustrial, reload: reloadEmpresas,
  } = useEmpresasIndustriais();
  // Ata reunião (Diogo): "uso da ECD para contas de despesa". Sem ECD processada, o
  // cabeçalho avisa (detalhes na aba Parâmetros).
  const { hasEcd, loading: loadingEcd } = useFormacaoPreco();

  // Ata: IS só para indústrias; industrial se QUALQUER empresa do projeto estiver marcada
  const isIndustrial = empresas.some((e) => e.atividadeIndustrial);
  const params = useMemo(() => ({ ...rawParams, isIndustrial }), [rawParams, isIndustrial]);

  const hasFilters = !!(from || to || selectedCompanies.length > 0);
  const hasRealData = fiscalData.mode === "real";
  const hasTotals = hasRealData && !!fiscalData.totalMensal;
  const clearFilters = () => { setFrom(""); setTo(""); setSelectedCompanies([]); };

  // Os meses do seletor vêm da consulta sem filtro; a filtrada só traz parte deles
  useEffect(() => {
    if (!hasFilters && hasRealData && fiscalData.dadosMensais?.length) {
      setAllMonths(fiscalData.dadosMensais.map((m) => m.periodo));
    }
  }, [hasFilters, hasRealData, fiscalData.dadosMensais]);

  useEffect(() => {
    if (!fiscalData.loading && VIEWS[view]?.real && !hasRealData) setView("dashboard");
  }, [fiscalData.loading, view, hasRealData]);

  const go = useCallback((next) => {
    setView(next);
    document.querySelector("[data-reforma-top]")?.scrollIntoView({ block: "start" });
  }, []);

  const activeTab = TABS.find((t) => t.views.includes(view));
  const canOpen = (key) => !VIEWS[key].real || hasRealData;
  const tabOptions = TABS.map((t) => {
    const enabled = t.views.some(canOpen);
    return { key: t.key, label: t.label, icon: t.icon, disabled: !enabled, title: enabled ? undefined : NO_DATA_HINT };
  });
  const selectTab = (key) => {
    const tab = TABS.find((t) => t.key === key);
    setView(tab.views.find(canOpen) || tab.views[0]);
  };

  const months = fiscalData.dadosMensais || [];
  const periodLabel = months.length > 0
    ? `${formatMonth(months[0].periodo)} — ${formatMonth(months[months.length - 1].periodo)} · ${months.length} ${months.length === 1 ? "mês" : "meses"}`
    : "";

  const firstLoad = fiscalData.mode === "loading";

  // Mesmo tratamento para o Dashboard e para as abas que dependem dos dados fiscais
  let fiscalGate = null;
  if (fiscalData.mode === "error") {
    fiscalGate = (
      <StateMessage
        icon={<FiAlertTriangle />}
        title="Não foi possível carregar os dados fiscais"
        sub="Verifique a conexão e tente de novo."
        action={<ActionButton onClick={fiscalData.reload} style={{ margin: "0 auto" }}><FiRefreshCw /> Tentar novamente</ActionButton>}
      />
    );
  } else if (firstLoad) {
    fiscalGate = <ReformaDashboardSkeleton />;
  } else if (fiscalData.mode === "mock" || (!hasTotals && !hasFilters)) {
    fiscalGate = <EmptyState screen="reforma-tributaria" />;
  } else if (!hasTotals) {
    fiscalGate = (
      <StateMessage
        icon={<FiDatabase />}
        title="Nenhum dado fiscal para estes filtros"
        sub="Mude o período ou a empresa para ver outros resultados."
        action={<ActionButton onClick={clearFilters} style={{ margin: "0 auto" }}><FiX /> Limpar filtros</ActionButton>}
      />
    );
  }

  let content;
  if (!project) {
    content = <StateMessage icon={<FiFolder />} title="Nenhum projeto selecionado" sub="Selecione um projeto para ver o impacto da reforma." />;
  } else if (VIEWS[view].filtered && fiscalGate) {
    content = fiscalGate;
  } else if (view === "dashboard") {
    content = (
      <ReformaDashboard
        fiscalData={fiscalData}
        params={params}
        isIndustrial={isIndustrial}
        hasEcd={loadingEcd || hasEcd}
        companyFiltered={selectedCompanies.length > 0}
        onGo={go}
      />
    );
  } else if (view === "simulacao") {
    content = (
      <SimulationDashboard
        fiscalData={fiscalData}
        isIndustrial={isIndustrial}
        companyFiltered={selectedCompanies.length > 0}
      />
    );
  } else if (view === "transicao-anual") {
    content = <TransitionDashboard fiscalData={fiscalData} companyFiltered={selectedCompanies.length > 0} />;
  } else if (view === "impacto-caixa") {
    content = <CashDashboard fiscalData={fiscalData} isIndustrial={isIndustrial} companyFiltered={selectedCompanies.length > 0} />;
  } else if (activeTab.sub) {
    content = (
      <>
        <Section $first>
          <SegmentedControl
            small
            value={view}
            onChange={setView}
            options={activeTab.sub.map((s) => ({ ...s, disabled: !canOpen(s.key), title: canOpen(s.key) ? undefined : NO_DATA_HINT }))}
          />
        </Section>
        {view === "formacao-preco" && <PriceDashboard />}
        {view === "cadastro-preco" && <CadastroPanel />}
        {view === "dados-mensais" && <MonthlyDashboard fiscalData={fiscalData} companyFiltered={selectedCompanies.length > 0} />}
        {view === "creditos-despesas" && <CreditsDashboard fiscalData={fiscalData} companyFiltered={selectedCompanies.length > 0} />}
      </>
    );
  } else {
    content = (
      <ParametrosDashboard
        fiscalData={fiscalData}
        empresas={empresas}
        loadingEmpresas={loadingEmpresas}
        empresasError={empresasError}
        onToggleIndustrial={toggleIndustrial}
        onRetryEmpresas={reloadEmpresas}
        isIndustrial={isIndustrial}
        hasEcd={loadingEcd ? null : hasEcd}
        hasFilters={hasFilters}
        onGo={go}
      />
    );
  }

  return (
    <CreditsPage>
      <div data-reforma-top style={{ scrollMarginTop: 200 }} />
      <ScrolledTopBar>
        <div className="title-row">
          <div>
            <div className="eyebrow">Vidal · Simulador CBS e IBS</div>
            <h1>Reforma Tributária</h1>
            <div className="meta">
              {fiscalData.mode === "error"
                ? <Pill $color={IOS.red}>Falha ao carregar</Pill>
                : <LiveDot>{firstLoad ? "Carregando os dados fiscais" : hasRealData ? "Hoje: SPEDs do projeto" : "Sem SPEDs processados"}</LiveDot>}
              {periodLabel && <span className="update-tag">{periodLabel}</span>}
              {!loadingEcd && !hasEcd && <Tag $color={IOS.orange} title="Sem ECD processada. Veja os detalhes em Parâmetros." style={{ marginLeft: 8 }}>ECD não importada</Tag>}
              <UpdatedAgo at={fiscalData.updatedAt} />
            </div>
          </div>
        </div>

        <div className="nav-row">
          <TabsScroll>
            <SegmentedControl value={activeTab.key} onChange={selectTab} options={tabOptions} />
          </TabsScroll>
          <Toolbar>
            {VIEWS[view].filtered && hasRealData && (
              <>
                <PeriodPicker
                  from={from}
                  to={to}
                  available={allMonths}
                  onChange={(a, b) => { setFrom(a); setTo(b); }}
                />
                <EmpresasFilter empresas={empresas} value={selectedCompanies} onChange={setSelectedCompanies} />
                {hasFilters && (
                  <RoundButton type="button" onClick={clearFilters} title="Limpar filtros" aria-label="Limpar filtros"><FiX /></RoundButton>
                )}
                <ToolbarDivider />
              </>
            )}
            <RoundButton type="button" onClick={fiscalData.reload} title="Atualizar" aria-label="Atualizar">
              <FiRefreshCw className={fiscalData.loading ? "spin" : ""} />
            </RoundButton>
          </Toolbar>
        </div>
      </ScrolledTopBar>

      <MotionScope>{content}</MotionScope>
    </CreditsPage>
  );
}

export default function ReformaTributaria() {
  const [project, setProject] = useState(currentProject);

  // A troca de projeto não dispara "storage" na mesma aba, daí o polling
  useEffect(() => {
    const sync = () => setProject(currentProject());
    window.addEventListener("storage", sync);
    const interval = setInterval(sync, 1000);
    return () => {
      window.removeEventListener("storage", sync);
      clearInterval(interval);
    };
  }, []);

  // A chave recomeça a tela: nada do projeto anterior continua aparecendo
  return (
    <ReformaParamsProvider key={project || "sem-projeto"}>
      <ReformaTributariaContent project={project} />
    </ReformaParamsProvider>
  );
}
